import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { CheckCircle2, Link2, Loader2, ShieldCheck } from 'lucide-react';

import axiosInstance from '../../api/api';
import { useSessionStore } from '../../store/sessionStore';
import AppBackButton from '../common/AppBackButton';
import { OAuthBrandIcon, type OAuthBrandId } from '../OAuthBrandIcons';
import {
  initiateFacebookOAuthLink,
  initiateGoogleOAuthLink,
  initiateLinkedInOAuthLink,
} from '../../services/oauthService';
import '../Settings/Settings.css';
import './AccountConnections.css';

type ProviderId = OAuthBrandId;

type CurrentUserResponse = {
  user?: {
    email?: string;
    authProvider?: string;
    auth_provider?: string;
  };
};

const PROVIDERS: {
  id: ProviderId;
  name: string;
  description: string;
  brandClass: string;
}[] = [
  {
    id: 'google',
    name: 'Google',
    description: 'Sign in with your Google account using the same email.',
    brandClass: 'ac-provider--google',
  },
  {
    id: 'linkedin',
    name: 'LinkedIn',
    description: 'Sign in with LinkedIn using the email on your SkipScreen account.',
    brandClass: 'ac-provider--linkedin',
  },
  {
    id: 'facebook',
    name: 'Facebook',
    description: 'Sign in with Facebook using the same email address.',
    brandClass: 'ac-provider--facebook',
  },
];

const LINK_HANDLERS: Record<ProviderId, () => Promise<void>> = {
  google: initiateGoogleOAuthLink,
  linkedin: initiateLinkedInOAuthLink,
  facebook: initiateFacebookOAuthLink,
};

const PROVIDER_LABELS: Record<ProviderId, string> = {
  google: 'Google',
  linkedin: 'LinkedIn',
  facebook: 'Facebook',
};

const AccountConnections: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [authProvider, setAuthProvider] = useState<string>('email');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState<ProviderId | null>(null);

  const loadUser = useCallback(async () => {
    try {
      const response = await axiosInstance.get<CurrentUserResponse>('user/current-user');
      const user = response.data.user;
      if (user) {
        setAuthProvider((user.authProvider || user.auth_provider || 'email').toLowerCase());
        setEmail(user.email || '');
      }
    } catch (err) {
      console.error('Failed to load current user', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadUser();
  }, [loadUser]);

  useEffect(() => {
    const linked = searchParams.get('linked') as ProviderId | null;
    const oauthError = searchParams.get('oauth_error');

    if (linked && PROVIDER_LABELS[linked]) {
      toast.success(`${PROVIDER_LABELS[linked]} is now your sign-in method.`);
      setAuthProvider(linked);
      void useSessionStore.getState().bootstrapSession({ force: true });
      void loadUser();
    } else if (oauthError) {
      toast.error(decodeURIComponent(oauthError));
    }

    if (linked || oauthError) {
      const next = new URLSearchParams(searchParams);
      next.delete('linked');
      next.delete('oauth_error');
      setSearchParams(next, { replace: true });
    }
  }, [loadUser, searchParams, setSearchParams]);

  const activeProvider = useMemo(() => {
    const p = authProvider.toLowerCase();
    if (p === 'google' || p === 'linkedin' || p === 'facebook') return p;
    return null;
  }, [authProvider]);

  async function handleConnect(provider: ProviderId) {
    if (connecting) return;
    setConnecting(provider);
    try {
      await LINK_HANDLERS[provider]();
    } catch (err: unknown) {
      const message = (err as Error)?.message || `Could not connect ${PROVIDER_LABELS[provider]}.`;
      toast.error(message);
      setConnecting(null);
    }
  }

  return (
    <div className="ac-page">
      <div className="ac-page-inner">
        <AppBackButton />

        <header className="ac-header">
          <h1>Connected accounts</h1>
          <p>
            Link a social account to sign in faster. Use the same email as your SkipScreen account (
            {email || 'your account email'}).
          </p>
        </header>

        {activeProvider ? (
          <div className="ac-banner">
            <ShieldCheck size={18} aria-hidden />
            <p>
              You currently sign in with <strong>{PROVIDER_LABELS[activeProvider]}</strong>.
              Connecting another provider will switch your sign-in method to that account.
            </p>
          </div>
        ) : (
          <div className="ac-banner ac-banner--muted">
            <Link2 size={18} aria-hidden />
            <p>
              Your account uses email and password. Connect Google, LinkedIn, or Facebook to sign in
              with one click.
            </p>
          </div>
        )}

        {loading ? (
          <div className="ac-loading">
            <Loader2 className="us-spin" size={20} />
            <span>Loading connected accounts…</span>
          </div>
        ) : (
          <ul className="ac-provider-list">
            {PROVIDERS.map((provider) => {
              const isConnected = activeProvider === provider.id;
              const isBusy = connecting === provider.id;
              const canSwitch = activeProvider !== null && activeProvider !== provider.id;

              return (
                <li key={provider.id}>
                  <article
                    className={`ac-provider ${provider.brandClass} ${
                      isConnected ? 'ac-provider--connected' : ''
                    }`}
                  >
                    <div className="ac-provider-main">
                      <div className="ac-provider-logo" aria-hidden>
                        <OAuthBrandIcon brand={provider.id} size={26} />
                      </div>
                      <div>
                        <div className="ac-provider-title-row">
                          <h2>{provider.name}</h2>
                          {isConnected ? (
                            <span className="ac-status ac-status--connected">
                              <CheckCircle2 size={14} aria-hidden />
                              Connected
                            </span>
                          ) : (
                            <span className="ac-status">Not connected</span>
                          )}
                        </div>
                        <p className="ac-provider-desc">{provider.description}</p>
                        {canSwitch ? (
                          <p className="ac-provider-note">
                            Connecting will replace {PROVIDER_LABELS[activeProvider!]} as your
                            sign-in method.
                          </p>
                        ) : null}
                      </div>
                    </div>
                    <div className="ac-provider-actions">
                      {isConnected ? (
                        <button type="button" className="us-btn us-btn-outline" disabled>
                          Connected
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="us-btn us-btn-primary"
                          disabled={Boolean(connecting)}
                          onClick={() => void handleConnect(provider.id)}
                        >
                          {isBusy ? (
                            <>
                              <Loader2 className="us-spin" size={16} /> Redirecting…
                            </>
                          ) : (
                            'Connect'
                          )}
                        </button>
                      )}
                    </div>
                  </article>
                </li>
              );
            })}
          </ul>
        )}

        <div className="ac-footer-links">
          <button
            type="button"
            className="us-btn us-btn-outline"
            onClick={() => navigate('/user-settings')}
          >
            Account settings
          </button>
          <button
            type="button"
            className="us-btn us-btn-outline"
            onClick={() => navigate('/user-profile')}
          >
            Profile customization
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccountConnections;
